import React, { useState } from 'react';
import { Card, Button, Typography, Space, Divider, Form, Radio, Input, InputNumber, Alert, Upload, message, Popconfirm, Modal, Switch, Table, Tag, Transfer, Tooltip }  from 'antd';
import { ArrowLeftOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined, GlobalOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 指定App固定应用：开启指定App时必须展示，不可删除
const FIXED_LIMIT_APPS = [
    { id: 'mxafTnW92P', name: '牵心', company: '—', companyId: '—', platforms: ['ios', 'android'] },
    { id: 'ZKnF1F57Ae', name: '牵心Pro', company: '—', companyId: '—', platforms: ['ios', 'android'] },
];
const FIXED_LIMIT_APP_IDS = FIXED_LIMIT_APPS.map(a => a.id);

const AppConfigDetail = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('install'); // install, scope, setting
    const [installSteps, setInstallSteps] = useState([
        { id: 1, title: '步骤1', content: '请确保设备已接通电源', image: null }
    ]);
    const [selectedStepId, setSelectedStepId] = useState(1);
    const [i18nModalVisible, setI18nModalVisible] = useState(false);

    // 模拟自己的App数据限制：设置产品在指定 App 激活使用同步到对应 App 列表
    const [myApps] = useState([
        { id: 'app_001', name: '鹤梦智能家', company: '鹤梦云端科技有限公司', companyId: 'CORP_882931', platforms: ['ios', 'android'] },
        { id: 'app_002', name: '鹤梦专业版', company: '鹤梦云端科技有限公司', companyId: 'CORP_882931', platforms: ['ios', 'android', 'pc'] },
        { id: 'app_003', name: '鹤梦海外版', company: '鹤梦云端科技有限公司', companyId: 'CORP_882931', platforms: ['ios', 'android'] },
    ]);

    // 限制名单数据
    const [limitApps, setLimitApps] = useState([]);
    
    // 弹窗状态
    const [appLimitEnabled, setAppLimitEnabled] = useState(false);
    const [ownAppModalVisible, setOwnAppModalVisible] = useState(false);
    const [otherAppModalVisible, setOtherAppModalVisible] = useState(false);
    const [targetKeys, setTargetKeys] = useState([]);

    // 查找其他App状态
    const [searchAppName, setSearchAppName] = useState('');
    const [foundApp, setFoundApp] = useState(null);

    const handleAddLimitApp = (app) => {
        if (limitApps.find(item => item.id === app.id)) {
            message.warning('该 App 已在名单中');
            return;
        }
        setLimitApps([...limitApps, app]);
        message.success('已添加到名单');
    };

    const handleSearchApp = () => {
        if (!searchAppName.trim()) {
            message.warning('请输入应用名称');
            return;
        }
        // 模拟查找
        setFoundApp({
            id: 'app_' + Date.now(),
            name: searchAppName,
            company: '外部协作企业',
            companyId: `CORP_${Math.floor(Math.random() * 900000 + 100000)}`,
            platforms: ['ios', 'android']
        });
    };

    const handleSaveOwnApps = () => {
        const selectedApps = myApps.filter(app => targetKeys.includes(app.id));
        // 合并去重
        const newList = [...limitApps];
        selectedApps.forEach(app => {
            if (!newList.find(item => item.id === app.id)) {
                newList.push(app);
            }
        });
        setLimitApps(newList);
        setOwnAppModalVisible(false);
        message.success('自有App添加成功');
    };

    const handleSaveOtherApp = () => {
        if (foundApp) {
            handleAddLimitApp(foundApp);
            setOtherAppModalVisible(false);
            setSearchAppName('');
            setFoundApp(null);
        }
    };

    const removeLimitApp = (id) => {
        if (FIXED_LIMIT_APP_IDS.includes(id)) {
            message.warning('牵心和牵心Pro为固定应用，不可移除');
            return;
        }
        setLimitApps(limitApps.filter(item => item.id !== id));
        message.info('已从名单移除');
    };

    // 指定App开关处理：开启时自动加入固定应用
    const handleAppLimitChange = (checked) => {
        setAppLimitEnabled(checked);
        if (checked) {
            setLimitApps(prev => {
                const nonFixed = prev.filter(a => !FIXED_LIMIT_APP_IDS.includes(a.id));
                return [...FIXED_LIMIT_APPS, ...nonFixed];
            });
        } else {
            setLimitApps([]);
        }
    };

    const languages = [
        { key: 'en', label: '英语' },
        { key: 'zh-Hant', label: '中文繁体' },
        { key: 'fr', label: '法语' },
        { key: 'ja', label: '日语' },
        { key: 'es', label: '西班牙语' },
        { key: 'ko', label: '韩语' },
        { key: 'de', label: '德语' },
        { key: 'it', label: '意大利语' },
        { key: 'ru', label: '俄语' },
        { key: 'pt', label: '葡萄牙语' },
        { key: 'vi', label: '越南语' },
        { key: 'th', label: '泰语' },
        { key: 'tr', label: '土耳其语' },
        { key: 'fa', label: '波斯语' },
        { key: 'id', label: '印尼语' },
    ];

    // 默认生效配置状态
    const [config, setConfig] = useState({
        resolution: 'standard',
        imageInterpolation: false,
        videoInterpolation: false,
        fullColorMode: 'timer',
        playDurationLimit: false,
        playDuration: 0,
        alarmShield: false,
        tripleEyeEnabled: false,
        originalStreamId: 1,
        cropPercentage: 70,
        lampFlag: 0,
        lightMaxTime: 43200
    });

    const handleConfigChange = (key, value) => {
        let newConfig = { ...config, [key]: value };
        if (key === 'videoInterpolation' && value === true) {
            newConfig.imageInterpolation = true;
        }
        setConfig(newConfig);
    };

    const handleAddStep = () => {
        const newStep = { 
            id: Date.now(), 
            title: `步骤${installSteps.length + 1}`, 
            content: '', 
            image: null 
        };
        setInstallSteps([...installSteps, newStep]);
        setSelectedStepId(newStep.id);
    };

    const handleDeleteStep = (id, e) => {
        e.stopPropagation();
        const newSteps = installSteps.filter(s => s.id !== id);
        setInstallSteps(newSteps);
        if (selectedStepId === id) {
            setSelectedStepId(newSteps.length > 0 ? newSteps[0].id : null);
        }
    };

    return (
        <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space size="large">
                        <Button 
                            type="text" 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate(-1)}
                            style={{ fontSize: '16px' }}
                        >
                            返回产品列表
                        </Button>
                        <Divider type="vertical" style={{ height: 24 }} />
                        <div>
                            <Title level={4} style={{ margin: 0 }}>App 功能配置</Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>配置产品在 App 端的交互与展示</Text>
                        </div>
                    </Space>
                </div>
            </Card>

            <div style={{ display: 'flex', gap: 16 }}>
                {/* 左侧菜单 */}
                <Card bordered={false} style={{ width: 240, borderRadius: 8, height: 'fit-content' }} bodyStyle={{ padding: '8px' }}>
                    {[
                        { key: 'install', label: '安装指引', desc: '引导用户完成安装' },
                        { key: 'scope', label: '应用App', desc: '设置产品使用权限' },
                        { key: 'setting', label: '默认配置', desc: 'App 基础交互逻辑' },
                    ].map(item => (
                        <div 
                            key={item.key}
                            onClick={() => setActiveSection(item.key)}
                            style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                marginBottom: '4px',
                                backgroundColor: activeSection === item.key ? '#e6f7ff' : 'transparent',
                                transition: 'all 0.3s'
                            }}
                        >
                            <div style={{ color: activeSection === item.key ? '#1890ff' : 'rgba(0,0,0,0.85)', fontWeight: activeSection === item.key ? 600 : 400 }}>
                                {item.label}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{item.desc}</div>
                        </div>
                    ))}
                </Card>

                {/* 右侧内容 */}
                <Card bordered={false} style={{ flex: 1, borderRadius: 8, minHeight: 600, display: 'flex', flexDirection: 'column' }} bodyStyle={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1 }}>
                        {activeSection === 'install' && (
                            <div style={{ display: 'flex', gap: 24 }}>
                                {/* 步骤选择列表 */}
                                <div style={{ width: 200, borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <span style={{ fontWeight: 600 }}>指引步骤</span>
                                        <Button type="primary" size="small" icon={<PlusOutlined />} shape="circle" onClick={handleAddStep} />
                                    </div>
                                    {installSteps.map((step, index) => (
                                        <div 
                                            key={step.id}
                                            onClick={() => setSelectedStepId(step.id)}
                                            style={{
                                                padding: '8px 12px',
                                                marginBottom: 8,
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                backgroundColor: selectedStepId === step.id ? '#f0faff' : '#fff',
                                                border: selectedStepId === step.id ? '1px solid #91d5ff' : '1px solid transparent'
                                            }}
                                        >
                                            <Text ellipsis style={{ width: 120 }}>{step.title}</Text>
                                            <Popconfirm title="确定删除？" onConfirm={(e) => handleDeleteStep(step.id, e)}>
                                                <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />} onClick={e => e.stopPropagation()} />
                                            </Popconfirm>
                                        </div>
                                    ))}
                                </div>

                                {/* 步骤编辑区 */}
                                <div style={{ flex: 1 }}>
                                    {selectedStepId ? (
                                        <Form layout="vertical">
                                            <Alert 
                                                message="安装指引会在 APP 设备激活页面展示，请确保文案准确且配图清晰。" 
                                                type="info" showIcon style={{ marginBottom: 24 }} 
                                            />
                                            <Form.Item label="步骤标题">
                                                <Input 
                                                    value={installSteps.find(s => s.id === selectedStepId)?.title}
                                                    onChange={(e) => setInstallSteps(installSteps.map(s => s.id === selectedStepId ? { ...s, title: e.target.value } : s))}
                                                />
                                            </Form.Item>
                                            <Form.Item label="指引资源（图片/视频）">
                                                <Upload.Dragger style={{ width: '100%', maxHeight: 200 }}>
                                                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                    <p className="ant-upload-text">点击或拖拽上传</p>
                                                </Upload.Dragger>
                                            </Form.Item>
                                            <Form.Item label="安装指引文案">
                                                <TextArea 
                                                    rows={4} 
                                                    maxLength={500} 
                                                    showCount 
                                                    placeholder="请输入安装详细说明" 
                                                    value={installSteps.find(s => s.id === selectedStepId)?.content}
                                                    onChange={(e) => setInstallSteps(installSteps.map(s => s.id === selectedStepId ? { ...s, content: e.target.value } : s))}
                                                />
                                            </Form.Item>
                                            <Form.Item label="多语言支持">
                                                <Button 
                                                    icon={<GlobalOutlined />} 
                                                    onClick={() => setI18nModalVisible(true)}
                                                >
                                                    配置多语言翻译 (已配置 0/{languages.length} 种语言)
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#ccc', marginTop: 100 }}>请选择或添加一个步骤进行配置</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeSection === 'scope' && (
                            <div style={{ padding: '0 8px' }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    padding: '0 20px',
                                    backgroundColor: '#fafafa',
                                    borderRadius: '8px',
                                    marginBottom: 24,
                                    height: 72
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontWeight: 600, fontSize: '16px' }}>指定App</span>
                                        <Switch checked={appLimitEnabled} onChange={handleAppLimitChange} />
                                        <Text style={{ fontSize: '13px', marginLeft: 16, color: appLimitEnabled ? '#ff4d4f' : '#52c41a' }}>
                                            {appLimitEnabled ? '产品只能在下方列表内的 App 激活使用。' : '未指定App：产品可在鹤梦平台任意 App 激活使用。'}
                                        </Text>
                                    </div>
                                    {appLimitEnabled && (
                                        <Space>
                                            <Button type="primary" ghost onClick={() => {
                                                setTargetKeys(limitApps.filter(a => myApps.find(m => m.id === a.id)).map(a => a.id));
                                                setOwnAppModalVisible(true);
                                            }}>添加自有App</Button>
                                            <Button type="primary" ghost onClick={() => setOtherAppModalVisible(true)}>添加其他App</Button>
                                        </Space>
                                    )}
                                </div>

                                {appLimitEnabled && (
                                    <Table
                                        size="middle"
                                        dataSource={limitApps}
                                        pagination={false}
                                        rowKey="id"
                                        columns={[
                                            { 
                                                title: 'App 信息', 
                                                key: 'appInfo',
                                                render: (_, record) => (
                                                    <div>
                                                        <div style={{ fontWeight: 500, color: 'rgba(0,0,0,0.85)' }}>{record.name}</div>
                                                    </div>
                                                )
                                            },
                                            { 
                                                title: '系统平台/端', 
                                                dataIndex: 'platforms', 
                                                key: 'platforms',
                                                render: (platforms) => (
                                                    <Space size={[0, 4]} wrap>
                                                        {(platforms || ['ios', 'android']).map(p => {
                                                            const colorMap = {
                                                                'ios': 'blue', 'android': 'green', 'pc': 'cyan',
                                                                '小程序': 'purple', '网页': 'gold', '桌面应用': 'magenta'
                                                            };
                                                            return <Tag color={colorMap[p] || 'default'} key={p} style={{ fontSize: '11px', borderRadius: '4px' }}>{p}</Tag>;
                                                        })}
                                                    </Space>
                                                )
                                            },
                                            { 
                                                title: '所属企业', 
                                                key: 'company',
                                                render: (_, record) => (
                                                    <div>
                                                        <div style={{ color: 'rgba(0,0,0,0.65)' }}>{record.company || '未知企业'}</div>
                                                        <div style={{ fontSize: '12px', color: '#bfbfbf' }}>{record.companyId || 'ID: -'}</div>
                                                    </div>
                                                )
                                            },
                                            { 
                                                title: '操作', 
                                                key: 'action',
                                                width: 80,
                                                render: (_, record) => {
                                                    const isFixed = FIXED_LIMIT_APP_IDS.includes(record.id);
                                                    return isFixed ? (
                                                        <Tooltip title="牵心和牵心Pro为固定应用，不可移除">
                                                            <Button type="link" danger disabled style={{ padding: 0 }}>移除</Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Button type="link" danger onClick={() => removeLimitApp(record.id)} style={{ padding: 0 }}>移除</Button>
                                                    );
                                                }
                                            }
                                        ]}
                                        locale={{ emptyText: <div style={{ padding: '64px 0', textAlign: 'center' }}>
                                            <InboxOutlined style={{ fontSize: 40, color: '#e8e8e8', marginBottom: 16 }} />
                                            <div style={{ color: '#999' }}>暂无指定App，请点击上方按钮添加</div>
                                        </div> }}
                                    />
                                )}
                            </div>
                        )}

                        {activeSection === 'setting' && (
                            <div>
                                <div style={{ marginBottom: 32 }}>
                                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: 24, display: 'flex', alignItems: 'center' }}>
                                        <span style={{ width: 4, height: 16, backgroundColor: '#1890ff', marginRight: 8, borderRadius: 2 }}></span>
                                        默认生效配置
                                    </div>
                                    
                                    <Form layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 16 }} labelAlign="left" colon={false}>
                                        <Form.Item label="默认分辨率">
                                            <Radio.Group value={config.resolution} onChange={(e) => handleConfigChange('resolution', e.target.value)}>
                                                <Radio value="hd">超清 (HD) <Text type="secondary" style={{ fontSize: '12px', marginLeft: 4 }}>优先采用产品最高清晰度</Text></Radio>
                                                <Divider type="vertical" />
                                                <Radio value="standard">标清 (SD) <Text type="secondary" style={{ fontSize: '12px', marginLeft: 4 }}>采用次高清清晰度以节省流量</Text></Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="插值设置">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
                                                    <Space>
                                                        <span style={{ color: '#8c8c8c' }}>图片插值:</span>
                                                        <Radio.Group value={config.imageInterpolation} onChange={(e) => handleConfigChange('imageInterpolation', e.target.value)} disabled={config.videoInterpolation}>
                                                            <Radio value={true}>开启</Radio>
                                                            <Radio value={false}>关闭</Radio>
                                                        </Radio.Group>
                                                    </Space>
                                                    {config.imageInterpolation && (
                                                        <Space>
                                                            <span style={{ color: '#8c8c8c' }}>尺寸:</span>
                                                            <Input style={{ width: 100 }} suffix="H" placeholder="高度" />
                                                            <span style={{ color: '#bfbfbf' }}>×</span>
                                                            <Input style={{ width: 100 }} suffix="W" placeholder="宽度" />
                                                            <span style={{ color: '#8c8c8c' }}>px</span>
                                                        </Space>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
                                                    <Space>
                                                        <span style={{ color: '#8c8c8c' }}>视频插值:</span>
                                                        <Radio.Group value={config.videoInterpolation} onChange={(e) => handleConfigChange('videoInterpolation', e.target.value)}>
                                                            <Radio value={true}>开启</Radio>
                                                            <Radio value={false}>关闭</Radio>
                                                        </Radio.Group>
                                                    </Space>
                                                    <Text type="warning" style={{ fontSize: '12px' }}><InfoCircleOutlined style={{ marginRight: 4 }} />视频插值开启时，图片插值将强制同步开启且不可关闭</Text>
                                                </div>
                                            </div>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="全彩模式时段">
                                            <Space size="middle">
                                                <Radio.Group value={config.fullColorMode} onChange={(e) => handleConfigChange('fullColorMode', e.target.value)}>
                                                    <Radio value="night">夜间智能感知</Radio>
                                                    <Radio value="timer">定时开启</Radio>
                                                </Radio.Group>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>App设置具体值，此处只设置默认模式。</Text>
                                            </Space>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="App 安全策略">
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                                                    <span>视频播放时长限制</span>
                                                    <Radio.Group value={config.playDurationLimit} onChange={(e) => handleConfigChange('playDurationLimit', e.target.value)}>
                                                        <Radio value={true}>开启限制</Radio>
                                                        <Radio value={false}>无限制</Radio>
                                                    </Radio.Group>
                                                    {config.playDurationLimit && (
                                                        <Input style={{ width: 120 }} suffix="秒" value={config.playDuration} onChange={(e) => handleConfigChange('playDuration', e.target.value)} />
                                                    )}
                                                </div>
                                                {config.playDurationLimit && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 12 }}>
                                                        <span>预警开关</span>
                                                        <Radio.Group value={config.playAlertFlag} onChange={(e) => handleConfigChange('playAlertFlag', e.target.value)}>
                                                            <Radio value={true}>开启</Radio>
                                                            <Radio value={false}>关闭</Radio>
                                                        </Radio.Group>
                                                        {config.playAlertFlag && (
                                                            <Input style={{ width: 120 }} suffix="秒" value={config.playAlertDuration} onChange={(e) => {
                                                                const raw = e.target.value.replace(/\D/g, '');
                                                                const v = Number(raw);
                                                                if (v > Number(config.playDuration)) { message.warning('预警时长不可大于限制时长'); return; }
                                                                handleConfigChange('playAlertDuration', raw);
                                                            }} />
                                                        )}
                                                    </div>
                                                )}
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {config.playDurationLimit 
                                                        ? (config.playAlertFlag ? `播放将持续 ${config.playDuration} 秒，剩余 ${config.playAlertDuration} 秒时触发预警弹窗。` : '开启后，App 在持续播放一定时间后会弹窗询问用户是否继续，防止无效流量消耗。')
                                                        : '开启后，App 在持续播放一定时间后会弹窗询问用户是否继续，防止无效流量消耗。'}
                                                </Text>
                                            </Space>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="一键报警配置">
                                            <Space>
                                                <Radio.Group value={config.alarmShield} onChange={(e) => handleConfigChange('alarmShield', e.target.value)}>
                                                    <Radio value={true}>启用屏蔽</Radio>
                                                    <Radio value={false}>不屏蔽</Radio>
                                                </Radio.Group>
                                                <Text type="secondary" style={{ fontSize: '12px' }}><InfoCircleOutlined style={{ marginRight: 4 }} />开启后，App 控制面板上的紧急报警按钮将被隐藏。</Text>
                                            </Space>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="假三目设置">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
                                                    <Radio.Group value={config.tripleEyeEnabled} onChange={(e) => handleConfigChange('tripleEyeEnabled', e.target.value)}>
                                                        <Radio value={true}>开启</Radio>
                                                        <Radio value={false}>关闭</Radio>
                                                    </Radio.Group>
                                                    {config.tripleEyeEnabled && (
                                                        <Space size="middle">
                                                            <Space>
                                                                <span style={{ color: '#8c8c8c' }}>原始流ID:</span>
                                                                <Input style={{ width: 200 }} value={config.originalStreamId} onChange={(e) => handleConfigChange('originalStreamId', e.target.value.replace(/\D/g, ''))} addonBefore="第" addonAfter="个camera" />
                                                            </Space>
                                                            <Space>
                                                                <span style={{ color: '#8c8c8c' }}>截取百分比:</span>
                                                                <Input style={{ width: 100 }} value={config.cropPercentage} onChange={(e) => handleConfigChange('cropPercentage', e.target.value)} suffix="%" />
                                                            </Space>
                                                        </Space>
                                                    )}
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>用于多目产品在 App 端的混合流展示与裁剪逻辑配置。</Text>
                                            </div>
                                        </Form.Item>
                                        <Divider dashed />
                                        <Form.Item label="灯控开关显示">
                                            <Space size={24}>
                                                <Radio.Group value={config.lampFlag} onChange={(e) => handleConfigChange('lampFlag', e.target.value)}>
                                                    <Radio value={0}>不显示</Radio>
                                                    <Radio value={1}>显示</Radio>
                                                </Radio.Group>
                                                {config.lampFlag === 1 && (
                                                    <Space>
                                                        <Text style={{ color: '#8c8c8c' }}>灯光控制时长：</Text>
                                                        <InputNumber value={config.lightMaxTime} onChange={(v) => handleConfigChange('lightMaxTime', v)} min={0} max={86400} style={{ width: 140 }} addonAfter="秒" />
                                                    </Space>
                                                )}
                                            </Space>
                                            <div><Text type="secondary" style={{ fontSize: '12px' }}>App首页是否显示灯控开关；灯光控制时长取值 0~86400 秒，默认 43200</Text></div>
                                        </Form.Item>
                                    </Form>
                                </div>
                            </div>
                        )}
                    </div>

                    {(activeSection === 'install' || activeSection === 'setting') && (
                        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24, marginTop: 24, textAlign: 'center' }}>
                            <Button type="primary" size="large" style={{ width: 200 }} onClick={() => message.success('配置已保存')}>保存配置</Button>
                        </div>
                    )}
                </Card>
            </div>

            {/* 自有App弹窗 */}
            <Modal
                title="添加自有App"
                open={ownAppModalVisible}
                onCancel={() => setOwnAppModalVisible(false)}
                onOk={handleSaveOwnApps}
                width={600}
                okText="确认添加"
            >
                <div style={{ padding: '20px 0' }}>
                    <Transfer
                        dataSource={myApps.map(app => ({ key: app.id, title: app.name }))}
                        targetKeys={targetKeys}
                        onChange={keys => setTargetKeys(keys)}
                        render={item => item.title}
                        listStyle={{ width: 250, height: 300 }}
                        titles={['可选App', '已选App']}
                    />
                </div>
            </Modal>

            {/* 其他App弹窗 */}
            <Modal
                title="添加其他App"
                open={otherAppModalVisible}
                onCancel={() => {
                    setOtherAppModalVisible(false);
                    setSearchAppName('');
                    setFoundApp(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => setOtherAppModalVisible(false)}>取消</Button>,
                    <Button key="save" type="primary" disabled={!foundApp} onClick={handleSaveOtherApp}>保存</Button>
                ]}
                width={480}
            >
                <div style={{ padding: '20px 0' }}>
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ marginBottom: 8 }}>应用名称</div>
                        <Input.Search
                            placeholder="请输入应用名称查找"
                            value={searchAppName}
                            onChange={e => setSearchAppName(e.target.value)}
                            onSearch={handleSearchApp}
                            enterButton="查找"
                        />
                    </div>
                    {foundApp && (
                        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: '12px 16px', borderRadius: 4 }}>
                            <div style={{ color: '#52c41a', fontWeight: 600, marginBottom: 4 }}>查找成功</div>
                            <div>应用名称：{foundApp.name}</div>
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><GlobalOutlined style={{ color: '#1890ff' }} /><span>配置多语言翻译 - {installSteps.find(s => s.id === selectedStepId)?.title}</span></div>}
                open={i18nModalVisible}
                onCancel={() => setI18nModalVisible(false)}
                onOk={() => { message.success('多语言配置已保存'); setI18nModalVisible(false); }}
                width={850}
                bodyStyle={{ maxHeight: '600px', overflowY: 'auto', padding: '12px 24px' }}
                okText="保存配置"
                cancelText="取消"
            >
                <div style={{ marginBottom: 16 }}><Alert message="默认展示文案（中文）：" description={installSteps.find(s => s.id === selectedStepId)?.content || '暂无文案内容'} type="info" style={{ borderRadius: 6 }} /></div>
                <Form layout="vertical">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px 24px' }}>
                        {languages.map(lang => (
                            <Form.Item key={lang.key} label={<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}><span style={{ fontWeight: 500 }}>{lang.label}</span><Text type="secondary" style={{ fontSize: '12px' }}>{lang.key}</Text></div>} style={{ marginBottom: 0 }}>
                                <TextArea placeholder={`请输入${lang.label}翻译内容`} rows={2} style={{ borderRadius: 6, resize: 'none' }} />
                            </Form.Item>
                        ))}
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default AppConfigDetail;
